/* eslint-disable sonarjs/no-duplicate-string */
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';

import { isFeatureEnabled, SupportedFeatures } from '@/helpers/featureFlagUtils';
import { hasAccessToPackageOverview } from '@/helpers/hasAccessToPackageOverview';
import { hasVisibilityLightAccess } from '@/helpers/hasVisibilityLightAccess';
import { useAgencyStore } from '@/stores/agency/useAgencyStore';
import { useAgencyProfileStore } from '@/stores/agencyProfile/useAgencyProfileStore';
import { SalesforceHgInventoryFocusGroupEnum } from '@/types-agency';

export function isUnsupportedBrowser(): boolean {
  return /^.*((MSIE(?!\s11))+|(Trident\/[0-6]+)).*/i.test(navigator.userAgent);
}

export const vPlusRouteGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> => {
  const agencyStore = useAgencyStore();
  const isPremiumAgency = agencyStore.activeAgency?.isPremiumAgency ?? false;
  const isVisibilityLight = hasVisibilityLightAccess(agencyStore.activeProducts);
  const focusGroup = useAgencyProfileStore().agencyProfile?.hgInventoryFocusGroup;
  const isVisibilityPro = agencyStore.isVisibilityProPackage;
  const isAgencyInBuyDomain =
    focusGroup === SalesforceHgInventoryFocusGroupEnum.MixedFocus ||
    focusGroup === SalesforceHgInventoryFocusGroupEnum.Sale;

  if (!isPremiumAgency && !isVisibilityLight && !isVisibilityPro && !isAgencyInBuyDomain) {
    const locale = from.params.locale || to.params.locale;
    const path = `/${locale}/ubm/not-found`;
    return next({ path });
  }
  next();
};

export const referenceListingsGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> => {
  const agencyStore = useAgencyStore();
  const isPremiumAgency = agencyStore.activeAgency?.isPremiumAgency ?? false;
  const hasGoldPackage = agencyStore.isGoldPackage;
  const hasSilverPackage = agencyStore.isSilverPackage;
  const hasWelcomePackage = agencyStore.isWelcomePackage;
  const isVisibilityLight = hasVisibilityLightAccess(agencyStore.activeProducts);
  const hasAccess =
    isPremiumAgency || hasGoldPackage || hasSilverPackage || hasWelcomePackage || isVisibilityLight;

  if (!hasAccess) {
    const locale = from.params.locale || to.params.locale;
    const path = `/${locale}/ubm/not-found`;
    return next({ path });
  }
  next();
};

export const winningMandateRouteGuard = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
): void => {
  if (!isFeatureEnabled(SupportedFeatures.ENABLE_WINNING_MANDATE, to)) {
    const locale = from.params.locale || to.params.locale;
    const path = `/${locale}/ubm/not-found`;
    return next({ path });
  }
  next();
};

export const regionalHighlightRouteGuard = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
): void => {
  if (!isFeatureEnabled(SupportedFeatures.RH_SLOT_MANAGEMENT, to)) {
    const locale = from.params.locale || to.params.locale;
    const path = `/${locale}/ubm/not-found`;
    return next({ path });
  }
  next();
};

export const packageOverviewGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> => {
  const hasAccess = hasAccessToPackageOverview(useAgencyStore().activeProducts);

  if (!hasAccess) {
    const locale = from.params.locale || to.params.locale;
    const path = `/${locale}/ubm/not-found`;
    return next({ path });
  }
  next();
};
